import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Medal, Loader2, Newspaper, Users, Plus, Pencil, Trash2, Send, Mail, Upload, CheckCircle2, LogOut, Lock, Home, Image, Video, FolderOpen, Music, FileIcon, Activity, Ban, ShieldCheck, UserCog, MessageSquare, MailOpen, UserPlus, History, FileDown, Palette, Save, RotateCcw, Bell, BellRing, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { API, BACKEND, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import RichTextEditor from "@/components/RichTextEditor";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSiteTexts, useSiteTheme } from "@/lib/siteConfig";
import { CONTENT_GROUPS } from "@/lib/contentRegistry";
import { SiteLogo } from "@/components/SiteLogo";
import { clearSiteLogoCache } from "@/lib/siteConfig";
import SiteKitDashboard from "@/components/SiteKitDashboard";

const emptyNews = { title: "", date: "", body: "", image_url: "", event_id: "" };

function LoginGate({ onLogin }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password.trim());
      if (u.role !== "admin") { setError("Questo account non ha privilegi di amministratore."); return; }
      onLogin();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6 noise-bg">
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <Lock className="w-6 h-6 text-gold" />
          <span className="font-display text-2xl font-semibold">Area <span className="text-gold">Amministrazione</span></span>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#0F172A] p-8">
          <h1 className="font-display text-3xl">Accesso protetto</h1>
          <p className="text-muted-foreground text-sm mt-1">Inserisci le credenziali amministratore.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label htmlFor="ae">Email</Label><Input id="ae" data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" /></div>
            <div><Label htmlFor="ap">Password</Label><Input id="ap" data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" /></div>
            {error && <p data-testid="admin-login-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entra"}
            </Button>
          </form>
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors mt-6 inline-flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> Torna al sito</Link>
        </div>
      </div>
    </div>
  );
}

function NewsManager() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(emptyNews);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notifying, setNotifying] = useState(null);

  const load = () => api.get("/news").then((r) => setNews(r.data));
  useEffect(() => { load(); api.get("/events").then((r) => setEvents(r.data)); }, []);

  const reset = () => { setForm(emptyNews); setEditingId(null); };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Immagine caricata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title || !form.body) { toast.error("Titolo e corpo del testo sono obbligatori"); return; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/news/${editingId}`, form); toast.success("Articolo aggiornato"); }
      else { await api.post("/news", form); toast.success("Articolo pubblicato"); }
      reset(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const edit = (n) => { setEditingId(n.id); setForm({ title: n.title, date: n.date || "", body: n.body, image_url: n.image_url || "", event_id: n.event_id || "", discipline: n.discipline || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { await api.delete(`/news/${id}`); toast.success("Articolo eliminato"); load(); };

  const notify = async (id) => {
    setNotifying(id);
    try {
      const { data } = await api.post(`/news/${id}/notify`, { site_url: window.location.origin });
      if (data.sent === 0) toast.info("Nessun iscritto alla newsletter a cui inviare. Aggiungi contatti dal sito.");
      else toast.success(`Email inviata a ${data.sent} iscritti${data.failed ? ` (${data.failed} non riuscite)` : ""}. Se non la vedi, controlla la cartella SPAM/Promozioni.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setNotifying(null); }
  };

  const testSend = async (id) => {
    const email = window.prompt("Inserisci l'email a cui inviare una copia di prova:");
    if (!email) return;
    try {
      await api.post(`/news/${id}/test-send`, { email: email.trim().toLowerCase(), site_url: window.location.origin });
      toast.success(`Email di prova inviata a ${email}. Controlla anche SPAM/Promozioni.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Form */}
      <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6 h-fit">
        <h3 className="text-lg font-semibold mb-4">{editingId ? "Modifica articolo" : "Scrivi un nuovo articolo"}</h3>
        <div className="space-y-4">
          <div><Label>Titolo</Label><Input data-testid="admin-news-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Data</Label><Input data-testid="admin-news-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
          <div>
            <Label>Corpo del testo</Label>
            <div className="mt-1.5"><RichTextEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} /></div>
            <p className="text-[11px] text-muted-foreground mt-1">Formatta il testo (grassetto, elenchi) e inserisci immagini col pulsante immagine: verranno salvate e mostrate nel punto scelto dell'articolo.</p>
          </div>
          <div>
            <Label>Evento collegato (per il link diretto d'iscrizione)</Label>
            <Select value={form.event_id || "none"} onValueChange={(v) => setForm({ ...form, event_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="admin-news-event" className="mt-1.5"><SelectValue placeholder="Nessun evento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun evento</SelectItem>
                {events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Disciplina (per l'invio mirato della newsletter)</Label>
            <Select value={form.discipline || "generale"} onValueChange={(v) => setForm({ ...form, discipline: v === "generale" ? "" : v })}>
              <SelectTrigger data-testid="admin-news-discipline" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="generale">Generale (a tutti gli iscritti)</SelectItem>
                <SelectItem value="artistica">Ginnastica Artistica</SelectItem>
                <SelectItem value="ritmica">Ginnastica Ritmica</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Se scegli una disciplina, l'email verrà inviata solo agli iscritti interessati a quella disciplina.</p>
          </div>
          <div>
            <Label>Immagine di copertina</Label>
            <label data-testid="admin-news-cover-drop" className="mt-1.5 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 hover:border-gold/50 rounded-lg py-5 cursor-pointer transition-colors">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin text-gold" /> : form.image_url ? <CheckCircle2 className="w-6 h-6 text-[#10B981]" /> : <Upload className="w-6 h-6 text-gold" />}
              <span className="text-sm text-muted-foreground">{uploading ? "Caricamento…" : form.image_url ? "Immagine caricata" : "Carica immagine di copertina"}</span>
              <input data-testid="admin-news-cover-input" type="file" className="hidden" onChange={(e) => uploadCover(e.target.files[0])} />
            </label>
            {form.image_url && <img src={mediaUrl(form.image_url)} alt="anteprima" className="mt-3 w-full h-36 object-cover rounded-lg border border-white/10" />}
          </div>
          <div className="flex gap-3">
            <Button data-testid="admin-news-save" onClick={save} disabled={saving} className="flex-1 bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Aggiorna" : "Pubblica articolo"}
            </Button>
            {editingId && <Button variant="outline" className="border-white/15" onClick={reset}>Annulla</Button>}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
        <h3 className="text-lg font-semibold mb-4">Articoli pubblicati ({news.length})</h3>
        {news.length === 0 ? (
          <p className="text-muted-foreground text-sm py-8 text-center">Nessun articolo.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent"><TableHead>Titolo</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {news.map((n) => (
                <TableRow key={n.id} data-testid={`admin-news-row-${n.id}`} className="border-white/10">
                  <TableCell className="font-medium max-w-[180px] truncate">{n.title}{n.notified_count ? <span className="block text-[11px] text-gold/70">inviato a {n.notified_count}</span> : null}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{n.date ? new Date(n.date).toLocaleDateString("it-IT") : (n.created_at ? new Date(n.created_at).toLocaleDateString("it-IT") : "—")}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button data-testid={`admin-news-test-${n.id}`} onClick={() => testSend(n.id)} title="Invia email di prova" className="text-muted-foreground hover:text-[#38BDF8] transition-colors"><Mail className="w-4 h-4" /></button>
                      <button data-testid={`admin-news-notify-${n.id}`} onClick={() => notify(n.id)} disabled={notifying === n.id} title="Invia agli iscritti" className="text-muted-foreground hover:text-gold transition-colors">{notifying === n.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
                      <button data-testid={`admin-news-edit-${n.id}`} onClick={() => edit(n)} className="text-muted-foreground hover:text-gold transition-colors"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`admin-news-delete-${n.id}`} onClick={() => remove(n.id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function MediaManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fullUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

  const load = () => api.get("/media").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Media caricato"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => { await api.delete(`/media/${id}`); toast.success("Media eliminato"); load(); };
  const setAsLogo = async (it) => {
    try {
      await api.put("/settings/logo", { logo_url: it.url });
      clearSiteLogoCache();
      toast.success("Logo del sito aggiornato. Ricarica per vederlo ovunque.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const copyCode = (it) => {
    const code = it.kind === "image" ? `[[img:${fullUrl(it.url)}]]` : it.kind === "video" ? `[[video:${fullUrl(it.url)}]]` : `[[file:${fullUrl(it.url)}|${it.original_filename || "Allegato"}]]`;
    try { navigator.clipboard?.writeText(code).then(() => toast.success("Codice copiato: incollalo nel testo dell'articolo")).catch(() => toast.info(`Codice: ${code}`)); }
    catch { toast.info(`Codice: ${code}`); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Libreria Media <span className="text-gold">({items.length})</span></h3>
        <label data-testid="media-manager-upload-drop" className="inline-flex items-center gap-2 bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold rounded-md px-4 py-2 cursor-pointer text-sm transition-colors">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Caricamento…" : "Carica media"}
          <input data-testid="media-manager-upload-input" type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} />
        </label>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Carica qualsiasi file (immagini, video, PDF, Word, Excel, PowerPoint, zip, audio…), poi copia il codice e incollalo nel testo di un articolo, oppure usa i pulsanti dell'editor.</p>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-white/10 rounded-lg"><FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />Nessun media caricato.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-manager-grid">
          {items.map((it) => (
            <div key={it.id} data-testid={`media-manager-item-${it.id}`} className="rounded-lg overflow-hidden border border-white/10 bg-black/30">
              <div className="aspect-video flex items-center justify-center bg-black/40">
                {it.kind === "image" ? <img src={fullUrl(it.url)} alt="" className="w-full h-full object-cover" />
                  : it.kind === "video" ? <Video className="w-8 h-8 text-gold" />
                  : it.kind === "audio" ? <Music className="w-8 h-8 text-gold" />
                  : <div className="flex flex-col items-center gap-1 text-gold"><FileIcon className="w-8 h-8" /><span className="text-[10px] font-bold uppercase tracking-wider">{it.ext || (it.original_filename || "").split(".").pop()}</span></div>}
              </div>
              <div className="p-2">
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground truncate mb-2">
                  {it.kind === "image" ? <Image className="w-3 h-3 shrink-0" /> : it.kind === "video" ? <Video className="w-3 h-3 shrink-0" /> : <FileIcon className="w-3 h-3 shrink-0" />}
                  <span className="truncate">{it.original_filename}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid={`media-manager-copy-${it.id}`} onClick={() => copyCode(it)} variant="outline" size="sm" className="flex-1 h-7 text-xs border-white/15 hover:border-gold/60">Copia codice</Button>
                  <button data-testid={`media-manager-delete-${it.id}`} onClick={() => remove(it.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                {it.kind === "image" && (
                  <Button data-testid={`media-manager-setlogo-${it.id}`} onClick={() => setAsLogo(it)} variant="ghost" size="sm" className="w-full h-7 text-xs mt-1.5 text-gold hover:bg-gold/10">
                    <Image className="w-3 h-3 mr-1" /> Imposta come logo
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessagesManager({ onUnreadChange }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [reasonFilter, setReasonFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [recipient, setRecipient] = useState("");
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [regPrefs, setRegPrefs] = useState({ enabled: true, disciplines: [], event_ids: [], mode: "digest" });
  const [prefEvents, setPrefEvents] = useState([]);
  const [digestState, setDigestState] = useState({});
  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post(`/contact/digest/send-now?site_url=${encodeURIComponent(window.location.origin)}`);
      setDigestState(data.digest || {});
      if (data.sent) toast.success(`Riepilogo inviato: ${data.count} iscrizioni`);
      else toast.info(data.reason === "disattivato" ? "Riepilogo disattivato nelle preferenze" : "Nessuna nuova iscrizione dall'ultimo riepilogo: email non inviata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setSendingDigest(false); }
  };

  const syncUnread = (list) => onUnreadChange?.(list.filter((m) => !m.read).length);
  const load = () => api.get("/contact").then((r) => { setMsgs(r.data); syncUnread(r.data); }).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/contact/settings").then((r) => { setRecipient((r.data.recipients || []).join("\n")); if (r.data.registrations) setRegPrefs(r.data.registrations); setDigestState(r.data.digest || {}); }).catch(() => {});
    api.get("/events").then((r) => setPrefEvents(r.data || [])).catch(() => {});
  }, []);

  const update = (list) => { setMsgs(list); syncUnread(list); };
  const unreadCount = msgs.filter((m) => !m.read).length;

  const REASON_LABELS = { prova_gratuita: "Prova gratuita", domanda_generica: "Domanda generica" };
  const filtered = msgs.filter((m) =>
    (reasonFilter === "all" || m.reason === reasonFilter) &&
    (statusFilter === "all" || (statusFilter === "unread" ? !m.read : m.read))
  );

  const saveRecipient = async () => {
    setSavingRecipient(true);
    try {
      const recipients = recipient.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/contact/settings", { recipients, registrations: regPrefs, site_url: window.location.origin });
      setRecipient((data.recipients || []).join("\n"));
      if (data.registrations) setRegPrefs(data.registrations);
      toast.success("Preferenze notifiche aggiornate");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel salvataggio"); }
    finally { setSavingRecipient(false); }
  };

  const toggleRead = async (m) => {
    await api.put(`/contact/${m.id}/read`, { read: !m.read });
    update(msgs.map((x) => (x.id === m.id ? { ...x, read: !m.read } : x)));
  };
  const openMsg = async (m) => {
    const next = openId === m.id ? null : m.id;
    setOpenId(next);
    if (next && !m.read) {
      await api.put(`/contact/${m.id}/read`, { read: true });
      update(msgs.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
    }
  };
  const remove = async (id) => {
    await api.delete(`/contact/${id}`);
    toast.success("Messaggio eliminato");
    update(msgs.filter((x) => x.id !== id));
  };
  const exportCsv = () => {
    const rows = [["Nome", "Cognome", "Email", "Oggetto", "Ragione", "Messaggio", "Data", "Letto"],
      ...filtered.map((m) => [m.first_name, m.last_name, m.email, m.subject, m.reason_label, m.message, new Date(m.created_at).toLocaleString("it-IT"), m.read ? "Sì" : "No"])];
    const csv = rows.map((r) => r.map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "messaggi-contatti.csv"; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
        <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-gold" /><h3 className="text-sm font-semibold">Destinatari notifiche email</h3></div>
        <p className="text-xs text-muted-foreground mb-3">Gli indirizzi email che ricevono un avviso ad ogni <b>nuova iscrizione utente</b> e ad ogni <b>nuovo messaggio</b> dal form Contatti. Inserisci un indirizzo per riga (oppure separati da virgola).</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Textarea data-testid="admin-notify-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} rows={2} placeholder={"email1@esempio.it\nemail2@esempio.it"} className="bg-[#0B1220] border-white/15" />
          <Button data-testid="admin-notify-save" onClick={saveRecipient} disabled={savingRecipient} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold shrink-0">{savingRecipient ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
        </div>

        <div className="mt-6 pt-5 border-t border-white/10" data-testid="registration-prefs">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold flex items-center gap-2"><BellRing className="w-4 h-4 text-gold" /> Email per le iscrizioni atleti</h4>
              <p className="text-xs text-muted-foreground mt-1">Scegli come e per quali iscrizioni ricevere l'email. La notifica resta comunque sempre visibile nella casella Notifiche.</p>
            </div>
            <Switch data-testid="reg-prefs-enabled" checked={regPrefs.enabled} onCheckedChange={(v) => setRegPrefs((p) => ({ ...p, enabled: v }))} />
          </div>
          {regPrefs.enabled && (
            <div className="mt-4 rounded-lg border border-white/10 bg-[#0B1220] p-3" data-testid="reg-prefs-mode">
              <Label className="text-xs">Frequenza email</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {[["digest", "Riepilogo giornaliero", "Un'unica email alle 20:00 (ora italiana) con tutte le nuove iscrizioni del giorno + file Excel allegato per la segreteria. Nessuna email se non ci sono iscrizioni."],
                  ["immediate", "Email immediata", "Un'email per ogni singola iscrizione, appena viene registrata."]].map(([k, label, desc]) => (
                  <button key={k} type="button" data-testid={`reg-prefs-mode-${k}`} onClick={() => setRegPrefs((p) => ({ ...p, mode: k }))}
                    className={`text-left rounded-lg border p-3 transition-colors ${regPrefs.mode === k ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/25"}`}>
                    <p className={`text-sm font-semibold ${regPrefs.mode === k ? "text-gold" : ""}`}>{label}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{desc}</p>
                  </button>
                ))}
              </div>
              {regPrefs.mode === "digest" && (
                <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                  <p className="text-[11px] text-muted-foreground" data-testid="reg-prefs-digest-state">
                    {digestState.last_run ? `Ultimo riepilogo: ${new Date(digestState.last_run).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })} · ${digestState.last_sent ? `${digestState.last_count} iscrizioni inviate` : "nessuna email (0 iscrizioni)"}` : "Nessun riepilogo inviato finora."}
                    {digestState.last_file_url && <> · <a data-testid="reg-prefs-digest-xlsx" href={mediaUrl(digestState.last_file_url)} className="text-gold underline">Scarica ultimo Excel</a></>}
                  </p>
                  <Button data-testid="reg-prefs-digest-send-now" size="sm" variant="outline" onClick={sendDigestNow} disabled={sendingDigest} className="border-gold/40 text-gold hover:bg-gold/10 h-8">
                    {sendingDigest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Invia riepilogo adesso
                  </Button>
                </div>
              )}
            </div>
          )}
          {regPrefs.enabled && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-xs">Disciplina</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[["artistica", "Artistica"], ["ritmica", "Ritmica"]].map(([k, label]) => {
                    const on = regPrefs.disciplines.length === 0 || regPrefs.disciplines.includes(k);
                    return (
                      <button key={k} type="button" data-testid={`reg-prefs-disc-${k}`} onClick={() => setRegPrefs((p) => {
                        const cur = p.disciplines.length ? p.disciplines : ["artistica", "ritmica"];
                        const next = cur.includes(k) ? cur.filter((d) => d !== k) : [...cur, k];
                        return { ...p, disciplines: next.length === 2 || next.length === 0 ? [] : next };
                      })} className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-muted-foreground"}`}>{label}</button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">{regPrefs.disciplines.length === 0 ? "Entrambe le discipline" : `Solo ${regPrefs.disciplines.join(" e ")}`}</p>
              </div>
              <div>
                <Label className="text-xs">Gare specifiche <span className="text-muted-foreground">(nessuna selezionata = tutte)</span></Label>
                <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-white/10 bg-[#0B1220] divide-y divide-white/5">
                  {prefEvents.length === 0 && <p className="text-xs text-muted-foreground p-3">Nessuna gara in calendario.</p>}
                  {prefEvents.map((ev) => {
                    const on = regPrefs.event_ids.includes(ev.id);
                    return (
                      <label key={ev.id} data-testid={`reg-prefs-event-${ev.id}`} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-white/5">
                        <input type="checkbox" checked={on} onChange={() => setRegPrefs((p) => ({ ...p, event_ids: on ? p.event_ids.filter((x) => x !== ev.id) : [...p.event_ids, ev.id] }))} className="accent-[#D4AF37]" />
                        <span className="truncate flex-1">{ev.title}</span>
                        <span className="text-muted-foreground shrink-0">{ev.date}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="reg-prefs-events-summary">{regPrefs.event_ids.length === 0 ? "Tutte le gare" : `${regPrefs.event_ids.length} gara/e selezionata/e`}</p>
              </div>
            </div>
          )}
          {!regPrefs.enabled && <p className="text-xs text-amber-300/90 mt-3">Email per le iscrizioni atleti disattivate: riceverai solo la notifica nella casella.</p>}
          <p className="text-[11px] text-muted-foreground mt-3">Ricorda di premere <b>Salva</b> qui sopra per applicare le preferenze.</p>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Messaggi ricevuti <span className="text-gold" data-testid="admin-messages-count">({msgs.length})</span>{unreadCount > 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gold/20 text-gold border border-gold/40" data-testid="admin-messages-unread">{unreadCount} non letti</span>}</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={reasonFilter} onValueChange={setReasonFilter}>
              <SelectTrigger data-testid="admin-messages-filter-reason" className="w-[180px] bg-[#0B1220] border-white/15 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le ragioni</SelectItem>
                <SelectItem value="prova_gratuita">Prova gratuita</SelectItem>
                <SelectItem value="domanda_generica">Domanda generica</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-messages-filter-status" className="w-[150px] bg-[#0B1220] border-white/15 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="unread">Non letti</SelectItem>
                <SelectItem value="read">Letti</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="admin-messages-export" onClick={exportCsv} variant="outline" className="border-white/15 hover:border-gold/60 h-9" disabled={filtered.length === 0}>Esporta CSV</Button>
          </div>
        </div>
        {msgs.length === 0 ? (
          <p className="text-muted-foreground text-sm py-8 text-center">Nessun messaggio ricevuto.</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground text-sm py-8 text-center" data-testid="admin-messages-empty-filter">Nessun messaggio corrisponde ai filtri.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((m) => (
              <div key={m.id} data-testid={`admin-message-${m.id}`} className={`rounded-lg border ${m.read ? "border-white/10 bg-transparent" : "border-gold/40 bg-gold/5"}`}>
                <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => openMsg(m)}>
                  {m.read ? <MailOpen className="w-4 h-4 text-muted-foreground shrink-0" /> : <Mail className="w-4 h-4 text-gold shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium truncate ${m.read ? "" : "text-gold"}`}>{m.first_name} {m.last_name}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-muted-foreground shrink-0">{m.reason_label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{m.subject || m.message}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{new Date(m.created_at).toLocaleDateString("it-IT")}</span>
                </div>
                {openId === m.id && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/10 space-y-2" data-testid={`admin-message-detail-${m.id}`}>
                    <p className="text-sm"><span className="text-muted-foreground">Email:</span> <a href={`mailto:${m.email}`} className="text-gold hover:underline">{m.email}</a></p>
                    {m.subject && <p className="text-sm"><span className="text-muted-foreground">Oggetto:</span> {m.subject}</p>}
                    <p className="text-sm whitespace-pre-wrap bg-[#0B1220]/60 rounded-lg p-3">{m.message}</p>
                    <div className="flex items-center gap-3 pt-1">
                      <button data-testid={`admin-message-toggle-${m.id}`} onClick={(e) => { e.stopPropagation(); toggleRead(m); }} className="text-xs text-muted-foreground hover:text-gold inline-flex items-center gap-1.5">
                        {m.read ? <><Mail className="w-3.5 h-3.5" /> Segna come non letto</> : <><MailOpen className="w-3.5 h-3.5" /> Segna come letto</>}
                      </button>
                      <button data-testid={`admin-message-delete-${m.id}`} onClick={(e) => { e.stopPropagation(); remove(m.id); }} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Elimina</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ContactsManager() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/newsletter").then((r) => setSubs(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { await api.delete(`/newsletter/${id}`); toast.success("Contatto rimosso"); load(); };

  const exportCsv = () => {
    const rows = [["Nome", "Email", "Data iscrizione"], ...subs.map((s) => [s.name, s.email, new Date(s.created_at).toLocaleString("it-IT")])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "contatti-newsletter.csv"; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Contatti raccolti <span className="text-gold" data-testid="admin-contacts-count">({subs.length})</span></h3>
        <Button data-testid="admin-contacts-export" onClick={exportCsv} variant="outline" className="border-white/15 hover:border-gold/60" disabled={subs.length === 0}>Esporta CSV</Button>
      </div>
      {subs.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Nessun contatto raccolto.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent"><TableHead>Nome e cognome</TableHead><TableHead>Email</TableHead><TableHead>Iscritto il</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {subs.map((s) => (
              <TableRow key={s.id} data-testid={`admin-contact-${s.id}`} className="border-white/10">
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-muted-foreground">{s.email}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{new Date(s.created_at).toLocaleDateString("it-IT")}</TableCell>
                <TableCell className="text-right"><button data-testid={`admin-contact-delete-${s.id}`} onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

const NOTIF_TYPES = {
  athlete: { label: "Iscrizioni atleti", short: "Atleti", icon: Users, c: "text-[#38BDF8]", ring: "border-[#38BDF8]/40 bg-[#38BDF8]/5" },
  user: { label: "Registrazioni utenti", short: "Utenti", icon: UserPlus, c: "text-[#10B981]", ring: "border-[#10B981]/40 bg-[#10B981]/5" },
  newsletter: { label: "Iscrizioni newsletter", short: "Newsletter", icon: Mail, c: "text-[#D4AF37]", ring: "border-[#D4AF37]/40 bg-[#D4AF37]/5" },
  contact: { label: "Messaggi contatti", short: "Messaggi", icon: MessageSquare, c: "text-[#F472B6]", ring: "border-[#F472B6]/40 bg-[#F472B6]/5" },
};

function timeAgo(iso) {
  const d = new Date(iso), now = new Date(), s = Math.floor((now - d) / 1000);
  if (s < 60) return "adesso";
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  if (s < 604800) return `${Math.floor(s / 86400)} g fa`;
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
}

function NotificationsManager({ onUnreadChange }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/notifications").then((r) => {
    setItems(r.data.items); setUnread(r.data.unread); onUnreadChange?.(r.data.unread);
  }).finally(() => setLoading(false));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const k of Object.keys(NOTIF_TYPES)) c[k] = items.filter((n) => n.type === k).length;
    return c;
  }, [items]);
  const filtered = filter === "all" ? items : items.filter((n) => n.type === filter);

  const applyLocal = (list) => { setItems(list); const u = list.filter((n) => !n.read).length; setUnread(u); onUnreadChange?.(u); };
  const toggleRead = async (n) => {
    applyLocal(items.map((x) => (x.id === n.id ? { ...x, read: !n.read } : x)));
    try { await api.put(`/notifications/${n.id}/read`, { read: !n.read }); } catch { load(); }
  };
  const markAllRead = async () => {
    setBusy(true);
    try { await api.put("/notifications/read-all"); applyLocal(items.map((x) => ({ ...x, read: true }))); toast.success("Tutte segnate come lette"); }
    catch (e) { toast.error("Errore"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    applyLocal(items.filter((x) => x.id !== id));
    try { await api.delete(`/notifications/${id}`); } catch { load(); }
  };
  const clearAll = async () => {
    if (!window.confirm(filter === "all" ? "Eliminare TUTTE le notifiche?" : `Eliminare tutte le notifiche "${NOTIF_TYPES[filter]?.label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/notifications${filter !== "all" ? `?type=${filter}` : ""}`);
      toast.success("Notifiche eliminate"); load();
    } catch (e) { toast.error("Errore"); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6" data-testid="notifications-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <BellRing className="w-5 h-5 text-gold" /> Casella notifiche
          {unread > 0 && <span data-testid="notif-unread-count" className="text-xs px-2 py-0.5 rounded-full bg-gold/20 text-gold border border-gold/40">{unread} non lette</span>}
        </h3>
        <div className="flex items-center gap-2">
          <Button data-testid="notif-mark-all-read" onClick={markAllRead} disabled={busy || unread === 0} variant="outline" size="sm" className="border-white/15 hover:border-gold/60 h-9"><CheckCheck className="w-4 h-4 mr-1.5" /> Segna tutte lette</Button>
          <Button data-testid="notif-clear-all" onClick={clearAll} disabled={busy || filtered.length === 0} variant="outline" size="sm" className="border-white/15 hover:border-destructive/60 hover:text-destructive h-9"><Trash2 className="w-4 h-4 mr-1.5" /> Svuota</Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Ogni iscrizione (atleti/gare, utenti, newsletter) e ogni messaggio genera qui una notifica e un'email agli indirizzi impostati nella scheda <b>Messaggi → Destinatari notifiche</b>.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        <button data-testid="notif-filter-all" onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${filter === "all" ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>Tutte ({counts.all})</button>
        {Object.entries(NOTIF_TYPES).map(([k, t]) => {
          const Icon = t.icon;
          return (
            <button key={k} data-testid={`notif-filter-${k}`} onClick={() => setFilter(k)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${filter === k ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>
              <Icon className={`w-3.5 h-3.5 ${filter === k ? "" : t.c}`} /> {t.short} ({counts[k] || 0})
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-14 text-muted-foreground border border-dashed border-white/10 rounded-lg">
          <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Nessuna notifica per ora. Le nuove iscrizioni compariranno qui.
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center" data-testid="notif-empty-filter">Nessuna notifica di questo tipo.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => {
            const t = NOTIF_TYPES[n.type] || { label: "Notifica", icon: Bell, c: "text-muted-foreground", ring: "border-white/10" };
            const Icon = t.icon;
            return (
              <div key={n.id} data-testid={`notif-item-${n.id}`} className={`flex items-start gap-3 p-4 rounded-lg border transition-colors ${n.read ? "border-white/10 bg-transparent" : t.ring}`}>
                <div className={`mt-0.5 shrink-0 ${n.read ? "text-muted-foreground" : t.c}`}><Icon className="w-5 h-5" /></div>
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => toggleRead(n)}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!n.read && <span className="w-2 h-2 rounded-full bg-gold shrink-0" data-testid={`notif-dot-${n.id}`} />}
                    <span className={`font-medium ${n.read ? "" : "text-foreground"}`}>{n.title}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-muted-foreground">{t.short}</span>
                  </div>
                  {n.description && <p className="text-sm text-muted-foreground mt-0.5">{n.description}</p>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(n.created_at)}</span>
                  <button data-testid={`notif-toggle-${n.id}`} onClick={() => toggleRead(n)} title={n.read ? "Segna come non letta" : "Segna come letta"} className="text-muted-foreground hover:text-gold transition-colors">{n.read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}</button>
                  <button data-testid={`notif-delete-${n.id}`} onClick={() => remove(n.id)} title="Elimina" className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ROLE = {
  admin: { label: "Amministratore", c: "text-[#D4AF37] border-[#D4AF37]/40" },
  direttore: { label: "Direttore di Giuria", c: "text-[#F472B6] border-[#F472B6]/40" },
  judge: { label: "Giudice", c: "text-[#38BDF8] border-[#38BDF8]/40" },
  segreteria: { label: "Segreteria", c: "text-[#10B981] border-[#10B981]/40" },
  public: { label: "Pubblico", c: "text-muted-foreground border-white/20" },
};

function AuditManager() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fAction, setFAction] = useState("all");
  const [fJudge, setFJudge] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  useEffect(() => { api.get("/scores/audit").then((r) => setLogs(r.data)).finally(() => setLoading(false)); }, []);

  const ACTION = {
    create: { label: "Creazione", c: "text-[#38BDF8] border-[#38BDF8]/40" },
    update: { label: "Modifica", c: "text-amber-400 border-amber-400/40" },
    confirm: { label: "Conferma", c: "text-[#10B981] border-[#10B981]/40" },
    unconfirm: { label: "Annulla conferma", c: "text-muted-foreground border-white/20" },
    delete: { label: "Eliminazione", c: "text-destructive border-destructive/40" },
    bulk_confirm: { label: "Conferma massiva", c: "text-[#10B981] border-[#10B981]/40" },
  };
  const roleLabel = (r) => ({ admin: "Amministratore", direttore: "Direttore", segreteria: "Segreteria", judge: "Giudice" }[r] || r);

  const judges = useMemo(() => [...new Set(logs.map((l) => l.judge_name).filter(Boolean))].sort(), [logs]);
  const filtered = useMemo(() => logs.filter((l) => {
    if (fAction !== "all" && l.action !== fAction) return false;
    if (fJudge !== "all" && l.judge_name !== fJudge) return false;
    if (fFrom && new Date(l.at).getTime() < new Date(fFrom + "T00:00:00").getTime()) return false;
    if (fTo && new Date(l.at).getTime() > new Date(fTo + "T23:59:59.999").getTime()) return false;
    return true;
  }), [logs, fAction, fJudge, fFrom, fTo]);
  const hasFilters = fAction !== "all" || fJudge !== "all" || !!fFrom || !!fTo;
  const reset = () => { setFAction("all"); setFJudge("all"); setFFrom(""); setFTo(""); };

  const exportPDF = () => {
    if (filtered.length === 0) { toast.error("Nessuna modifica da esportare"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const now = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
    doc.setFontSize(16); doc.setTextColor(20, 20, 20);
    doc.text("OPES Ginnastiche — Storico Modifiche Voti", 14, 16);
    const info = [];
    if (fAction !== "all") info.push(`Azione: ${ACTION[fAction]?.label || fAction}`);
    if (fJudge !== "all") info.push(`Giudice: ${fJudge}`);
    if (fFrom) info.push(`Dal: ${fFrom}`);
    if (fTo) info.push(`Al: ${fTo}`);
    doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(`Generato il ${now}${info.length ? "  •  " + info.join("  •  ") : ""}  •  ${filtered.length} voci`, 14, 22);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.5); doc.line(14, 25, 283, 25);
    autoTable(doc, {
      startY: 29,
      head: [["Data/ora", "Azione", "Autore", "Atleta", "Attrezzo", "Giudice", "Voto"]],
      body: filtered.map((l) => [
        new Date(l.at).toLocaleString("it-IT"),
        ACTION[l.action]?.label || l.action,
        `${l.actor_name} (${roleLabel(l.actor_role)})`,
        l.athlete_name || "—",
        l.apparatus || "—",
        l.judge_name || (l.action === "bulk_confirm" ? `${l.count || ""} voti` : "—"),
        l.action === "update" && l.old_total != null ? `${Number(l.old_total).toFixed(3)} \u2192 ${Number(l.new_total).toFixed(3)}`
          : l.new_total != null ? Number(l.new_total).toFixed(3)
          : l.old_total != null ? Number(l.old_total).toFixed(3) : "—",
      ]),
      theme: "grid",
      headStyles: { fillColor: [212, 175, 55], textColor: [2, 6, 23], fontStyle: "bold" },
      styles: { fontSize: 8, cellPadding: 1.5 },
      margin: { left: 14, right: 14 },
    });
    doc.save(`storico-voti-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF generato");
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6" data-testid="audit-manager">
      <h3 className="text-lg font-semibold mb-1">Storico modifiche voti <span className="text-gold">({filtered.length}{hasFilters ? ` / ${logs.length}` : ""})</span></h3>
      <p className="text-sm text-muted-foreground mb-4">Registro di chi ha inserito, modificato, confermato o eliminato un voto, con data e ora. Le modifiche del Direttore sovrascrivono i dati originali: qui ne trovi traccia.</p>
      {logs.length > 0 && (
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="audit-filters">
          <div>
            <Label className="text-xs text-muted-foreground">Azione</Label>
            <Select value={fAction} onValueChange={setFAction}>
              <SelectTrigger data-testid="audit-filter-action" className="w-44 mt-1 bg-[#020617] border-white/15"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le azioni</SelectItem>
                {Object.entries(ACTION).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Giudice</Label>
            <Select value={fJudge} onValueChange={setFJudge}>
              <SelectTrigger data-testid="audit-filter-judge" className="w-44 mt-1 bg-[#020617] border-white/15"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i giudici</SelectItem>
                {judges.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Dal</Label>
            <Input data-testid="audit-filter-from" type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="w-40 mt-1 bg-[#020617] border-white/15" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Al</Label>
            <Input data-testid="audit-filter-to" type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="w-40 mt-1 bg-[#020617] border-white/15" />
          </div>
          {hasFilters && <Button data-testid="audit-filter-reset" variant="outline" onClick={reset} className="border-white/15 hover:bg-white/5">Azzera filtri</Button>}
          <Button data-testid="audit-export-pdf" variant="outline" onClick={exportPDF} className="border-white/15 hover:bg-white/5 ml-auto"><FileDown className="w-4 h-4 mr-1.5" /> Esporta PDF</Button>
        </div>
      )}
      {logs.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Nessuna modifica registrata.</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center" data-testid="audit-no-match">Nessuna modifica corrisponde ai filtri selezionati.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead>Data/ora</TableHead><TableHead>Azione</TableHead><TableHead>Autore</TableHead>
              <TableHead>Atleta</TableHead><TableHead>Attrezzo</TableHead><TableHead>Giudice</TableHead><TableHead className="text-right">Voto</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((l) => {
              const act = ACTION[l.action] || { label: l.action, c: "text-muted-foreground border-white/20" };
              return (
                <TableRow key={l.id} data-testid={`audit-row-${l.id}`} className="border-white/10">
                  <TableCell className="text-muted-foreground text-xs whitespace-nowrap">{new Date(l.at).toLocaleString("it-IT")}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded-full border ${act.c}`}>{act.label}</span></TableCell>
                  <TableCell className="text-sm">{l.actor_name} <span className="text-xs text-muted-foreground">({roleLabel(l.actor_role)})</span></TableCell>
                  <TableCell className="text-sm">{l.athlete_name}</TableCell>
                  <TableCell className="text-xs capitalize text-muted-foreground">{l.apparatus || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{l.judge_name || (l.action === "bulk_confirm" ? `${l.count || ""} voti` : "—")}</TableCell>
                  <TableCell className="text-right text-sm">
                    {l.action === "update" && l.old_total != null
                      ? <span><span className="text-muted-foreground line-through">{Number(l.old_total).toFixed(3)}</span> → <span className="text-gold font-semibold">{Number(l.new_total).toFixed(3)}</span></span>
                      : l.new_total != null ? <span className="text-gold font-semibold">{Number(l.new_total).toFixed(3)}</span>
                      : l.old_total != null ? <span className="text-muted-foreground line-through">{Number(l.old_total).toFixed(3)}</span>
                      : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function UsersManager() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "public" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setMode("create"); setEditingId(null); setForm({ name: "", email: "", password: "", role: "public" }); setDialogOpen(true); };
  const openEdit = (u) => { setMode("edit"); setEditingId(u.id); setForm({ name: u.name, email: u.email, password: "", role: u.role }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome"); return; }
    if (mode === "create") {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) { toast.error("Inserisci un'email valida"); return; }
      if (form.password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    } else if (form.password && form.password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    setSaving(true);
    try {
      if (mode === "create") {
        await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role });
        toast.success("Utente creato");
      } else {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
        toast.success("Utente aggiornato");
      }
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/toggle-disable`);
      toast.success(data.disabled ? "Utente disabilitato" : "Utente riabilitato");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Eliminare definitivamente l'account di ${u.name} (${u.email})? L'operazione è irreversibile.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Account eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6" data-testid="users-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="text-lg font-semibold">Utenti registrati <span className="text-gold">({users.length})</span></h3>
        <Button data-testid="admin-user-create-btn" onClick={openCreate} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold"><UserPlus className="w-4 h-4 mr-1.5" /> Nuovo utente</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Crea nuovi account con un ruolo, modifica nome/ruolo/password, disabilita l'accesso o elimina un utente. Un account disabilitato non può accedere.</p>
      {users.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Nessun utente registrato.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead>
              <TableHead>Iscritto il</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const role = ROLE[u.role] || ROLE.public;
              const isSelf = me?.id === u.id;
              return (
                <TableRow key={u.id} data-testid={`admin-user-${u.id}`} className="border-white/10">
                  <TableCell className="font-medium">{u.name}{isSelf && <span className="text-[10px] text-gold ml-1">(tu)</span>}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded-full border ${role.c}`}>{role.label}</span></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                  <TableCell>
                    {u.disabled
                      ? <span className="text-xs text-destructive inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Disabilitato</span>
                      : <span className="text-xs text-[#10B981] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Attivo</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`admin-user-edit-${u.id}`} onClick={() => openEdit(u)} title="Modifica" className="text-muted-foreground hover:text-gold transition-colors"><Pencil className="w-4 h-4" /></button>
                      {!isSelf && (
                        <>
                          <Button data-testid={`admin-user-toggle-${u.id}`} onClick={() => toggle(u)} variant="outline" size="sm" className="h-7 text-xs border-white/15 hover:border-gold/60">
                            {u.disabled ? <><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Riabilita</> : <><Ban className="w-3.5 h-3.5 mr-1" /> Disabilita</>}
                          </Button>
                          <button data-testid={`admin-user-delete-${u.id}`} onClick={() => remove(u)} title="Elimina" className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="admin-user-dialog" className="bg-[#0F172A] border-white/10">
          <DialogHeader><DialogTitle className="font-display text-2xl">{mode === "create" ? "Nuovo utente" : "Modifica utente"}</DialogTitle>
            <DialogDescription>{mode === "create" ? "Crea un account e assegna un ruolo." : "Aggiorna nome, ruolo e (facoltativamente) la password."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome</Label><Input data-testid="admin-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label>Email</Label>
              <Input data-testid="admin-user-email-input" type="email" value={form.email} disabled={mode === "edit"} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 disabled:opacity-60" />
              {mode === "edit" && <p className="text-[11px] text-muted-foreground mt-1">L'email non è modificabile.</p>}
            </div>
            <div>
              <Label>{mode === "create" ? "Password" : "Nuova password (facoltativa)"}</Label>
              <Input data-testid="admin-user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder={mode === "edit" ? "Lascia vuoto per non modificare" : "Almeno 6 caratteri"} />
            </div>
            <div>
              <Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="admin-user-role-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="admin-user-role-admin">Amministratore</SelectItem>
                  <SelectItem value="direttore" data-testid="admin-user-role-direttore">Direttore di Giuria</SelectItem>
                  <SelectItem value="segreteria" data-testid="admin-user-role-segreteria">Segreteria</SelectItem>
                  <SelectItem value="judge" data-testid="admin-user-role-judge">Giudice</SelectItem>
                  <SelectItem value="public" data-testid="admin-user-role-public">Pubblico</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="admin-user-save-btn" onClick={save} disabled={saving} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "create" ? "Crea utente" : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AppearanceManager() {
  const { theme, setAccent } = useSiteTheme();
  const { texts, setText } = useSiteTexts();
  const [accent, setAccentDraft] = useState(theme.accent || "#D4AF37");
  const [savingAccent, setSavingAccent] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savingSlot, setSavingSlot] = useState(null);

  useEffect(() => { setAccentDraft(theme.accent || "#D4AF37"); }, [theme.accent]);

  const applyAccentLive = (color) => { setAccentDraft(color); setAccent(color); };
  const saveAccent = async () => {
    setSavingAccent(true);
    try {
      await api.put("/settings/theme", { accent });
      setAccent(accent);
      toast.success("Colore accento salvato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setSavingAccent(false); }
  };
  const resetAccent = () => applyAccentLive("#D4AF37");

  const val = (slot, def) => (drafts[slot] != null ? drafts[slot] : (texts[slot] != null && texts[slot] !== "" ? texts[slot] : def));
  const saveText = async (slot) => {
    setSavingSlot(slot);
    try {
      const value = drafts[slot] != null ? drafts[slot] : (texts[slot] ?? "");
      await api.put("/settings/text", { slot, value });
      setText(slot, value);
      setDrafts((d) => { const n = { ...d }; delete n[slot]; return n; });
      toast.success("Testo salvato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setSavingSlot(null); }
  };
  const resetSlot = async (slot) => {
    setSavingSlot(slot);
    try {
      await api.put("/settings/text", { slot, value: "" });
      setText(slot, "");
      setDrafts((d) => { const n = { ...d }; delete n[slot]; return n; });
      toast.success("Ripristinato al valore predefinito");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setSavingSlot(null); }
  };
  const resetGroup = async (items) => {
    try {
      await Promise.all(items.map((it) => api.put("/settings/text", { slot: it.slot, value: "" })));
      items.forEach((it) => setText(it.slot, ""));
      setDrafts((d) => { const n = { ...d }; items.forEach((it) => delete n[it.slot]); return n; });
      toast.success("Sezione ripristinata ai valori predefiniti");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="appearance-manager">
      <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
        <h3 className="text-lg font-semibold mb-1 flex items-center gap-2"><Palette className="w-5 h-5 text-gold" /> Colore accento</h3>
        <p className="text-sm text-muted-foreground mb-4">Il colore dorato usato per titoli, pulsanti ed evidenziazioni in tutto il sito. L'anteprima è immediata; premi Salva per renderlo permanente.</p>
        <div className="flex flex-wrap items-center gap-4">
          <input data-testid="accent-color-picker" type="color" value={accent} onChange={(e) => applyAccentLive(e.target.value)} className="w-14 h-10 rounded cursor-pointer bg-transparent border border-white/15" />
          <input data-testid="accent-color-hex" value={accent} onChange={(e) => applyAccentLive(e.target.value)} className="w-32 bg-[#020617] border border-white/15 rounded-lg px-3 py-2 text-sm font-mono" />
          <Button data-testid="accent-save-btn" onClick={saveAccent} disabled={savingAccent} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
            {savingAccent ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Salva colore</>}
          </Button>
          <Button data-testid="accent-reset-btn" onClick={resetAccent} variant="outline" className="border-white/15 hover:bg-white/5"><RotateCcw className="w-4 h-4 mr-1.5" /> Ripristina oro</Button>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
        <h3 className="text-lg font-semibold mb-1">Testi del sito</h3>
        <p className="text-sm text-muted-foreground mb-4">Modifica i testi delle pagine pubbliche. Puoi anche modificarli direttamente sulla pagina (icona matita) quando sei loggato come admin.</p>
        <div className="space-y-6">
          {CONTENT_GROUPS.map((g) => (
            <div key={g.group}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gold">{g.group}</h4>
                <Button data-testid={`content-reset-group-${g.group}`} onClick={() => resetGroup(g.items)} size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-gold h-7"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Ripristina sezione</Button>
              </div>
              <div className="space-y-3">
                {g.items.map((it) => (
                  <div key={it.slot} className="flex flex-col sm:flex-row sm:items-start gap-2" data-testid={`content-row-${it.slot}`}>
                    <label className="text-sm text-muted-foreground sm:w-64 sm:pt-2 shrink-0">{it.label}</label>
                    <div className="flex-1 flex gap-2">
                      {it.multiline ? (
                        <textarea data-testid={`content-input-${it.slot}`} rows={2} value={val(it.slot, it.default)} onChange={(e) => setDrafts((d) => ({ ...d, [it.slot]: e.target.value }))} className="flex-1 bg-[#020617] border border-white/15 rounded-lg px-3 py-2 text-sm" />
                      ) : (
                        <input data-testid={`content-input-${it.slot}`} value={val(it.slot, it.default)} onChange={(e) => setDrafts((d) => ({ ...d, [it.slot]: e.target.value }))} className="flex-1 bg-[#020617] border border-white/15 rounded-lg px-3 py-2 text-sm" />
                      )}
                      <Button data-testid={`content-save-${it.slot}`} onClick={() => saveText(it.slot)} disabled={savingSlot === it.slot} size="sm" variant="outline" className="border-white/15 hover:bg-white/5 shrink-0">
                        {savingSlot === it.slot ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      </Button>
                      <Button data-testid={`content-reset-${it.slot}`} onClick={() => resetSlot(it.slot)} disabled={savingSlot === it.slot} size="sm" variant="ghost" className="text-muted-foreground hover:text-gold shrink-0" title="Ripristina predefinito">
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user, loading, logout } = useAuth();
  const [authed, setAuthed] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/contact").then((r) => setUnread(r.data.filter((m) => !m.read).length)).catch(() => {});
      api.get("/notifications/unread-count").then((r) => setNotifUnread(r.data.count)).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Caricamento…</div>;

  const isAdmin = user?.role === "admin";
  if (!isAdmin && !authed) return <LoginGate onLogin={() => setAuthed(true)} />;
  if (!isAdmin) return <LoginGate onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-background text-foreground noise-bg">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <SiteLogo className="w-9 h-9" />
            <span className="font-display text-2xl font-semibold">Pannello <span className="text-gold">Admin</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Dashboard</Link>
            <button data-testid="admin-logout" onClick={logout} className="text-sm text-muted-foreground hover:text-destructive transition-colors inline-flex items-center gap-1.5"><LogOut className="w-4 h-4" /> Esci</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl mb-6">Pannello di controllo</motion.h1>
        <Tabs defaultValue="sitekit">
          <TabsList className="bg-[#0F172A] border border-white/10 h-auto p-1 mb-6 flex flex-wrap gap-1">
            <TabsTrigger data-testid="admin-tab-sitekit" value="sitekit" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><Activity className="w-4 h-4 mr-1.5" /> Monitoraggio</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-notifications" value="notifications" className="relative data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><Bell className="w-4 h-4 mr-1.5" /> Notifiche{notifUnread > 0 && <span data-testid="admin-notif-badge" className="ml-2 min-w-[18px] h-[18px] inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-gold text-[#020617] px-1">{notifUnread}</span>}</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-news" value="news" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><Newspaper className="w-4 h-4 mr-1.5" /> Gestione Notizie</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-media" value="media" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><Image className="w-4 h-4 mr-1.5" /> Media</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-messages" value="messages" className="relative data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><MessageSquare className="w-4 h-4 mr-1.5" /> Messaggi{unread > 0 && <span data-testid="admin-messages-badge" className="ml-2 min-w-[18px] h-[18px] inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-gold text-[#020617] data-[state=active]:bg-[#020617] data-[state=active]:text-gold px-1">{unread}</span>}</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-contacts" value="contacts" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><Users className="w-4 h-4 mr-1.5" /> Contatti</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-users" value="users" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><UserCog className="w-4 h-4 mr-1.5" /> Utenti</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-audit" value="audit" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><History className="w-4 h-4 mr-1.5" /> Storico Voti</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-appearance" value="appearance" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2"><Palette className="w-4 h-4 mr-1.5" /> Aspetto & Contenuti</TabsTrigger>
          </TabsList>
          <TabsContent value="sitekit"><SiteKitDashboard /></TabsContent>
          <TabsContent value="notifications"><NotificationsManager onUnreadChange={setNotifUnread} /></TabsContent>
          <TabsContent value="news"><NewsManager /></TabsContent>
          <TabsContent value="media"><MediaManager /></TabsContent>
          <TabsContent value="messages"><MessagesManager onUnreadChange={setUnread} /></TabsContent>
          <TabsContent value="contacts"><ContactsManager /></TabsContent>
          <TabsContent value="users"><UsersManager /></TabsContent>
          <TabsContent value="audit"><AuditManager /></TabsContent>
          <TabsContent value="appearance"><AppearanceManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
